'use client'

import { createContext, useContext, useEffect, useState } from 'react'
import { useAuth } from './auth-provider'
import { Company } from '@/types'
import { CompanyService } from '@/lib/database/company-service'

interface CompanyContextType {
  activeCompany: any
  availableCompanies: any[]
  setActiveCompany: (company: any) => void
  upsertCompany: (company: any) => void
  switchCompany: (companyId: string) => Promise<void>
  isLoading: boolean
  markAsFavorite: (companyId: string) => void
  archiveCompany: (companyId: string) => void
  deleteCompany: (companyId: string) => void
  toggleCompanyStatus: (companyId: string) => void
  getCompaniesByStatus: (status: 'active' | 'inactive' | 'archived') => any[]
  getFavoriteCompanies: () => any[]
  favoriteCompanyId: string | null
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined)

async function fetchLastActiveCompanyId(token: string): Promise<string | null> {
  try {
    const res = await fetch('/api/profile', {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    })
    if (!res.ok) return null
    const data = await res.json()
    return data?.last_active_company_id || null
  } catch {
    return null
  }
}

async function saveLastActiveCompanyId(token: string, companyId: string): Promise<void> {
  try {
    await fetch('/api/profile', {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ last_active_company_id: companyId }),
    })
  } catch {
    // ignore save failures
  }
}

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const { user, getAccessToken } = useAuth()
  const [activeCompany, setActiveCompanyState] = useState<any>(null)
  const [availableCompanies, setAvailableCompanies] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [favoriteCompanyId, setFavoriteCompanyId] = useState<string>('')

  useEffect(() => {
    const loadCompanies = async () => {
      if (!user) {
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)

        let companies: any[] = []
        const token = await getAccessToken()
        const hasRealToken = !!token
        if (token) {
          try {
            const res = await fetch('/api/companies', {
              headers: { Authorization: `Bearer ${token}` },
              cache: 'no-store',
            })
            if (res.ok) companies = await res.json()
          } catch { /* ignore */ }
        }

        if ((!Array.isArray(companies) || companies.length === 0) && !hasRealToken) {
          companies = CompanyService.getMockCompanies()
        }

        // Deduplicate by id
        const seen = new Set<string>()
        companies = companies.filter((c: any) => {
          if (!c?.id || seen.has(c.id)) return false
          seen.add(c.id)
          return true
        })

        setAvailableCompanies(companies)

        // Restore active company from DB
        let nextActiveCompany: any = null
        if (token) {
          const lastActiveId = await fetchLastActiveCompanyId(token)
          if (lastActiveId) {
            nextActiveCompany = companies.find((c: any) => c.id === lastActiveId) || null
          }
        }

        if (!nextActiveCompany && companies.length > 0) {
          nextActiveCompany = companies[0]
        }

        if (nextActiveCompany) {
          setActiveCompanyState(nextActiveCompany)
        }

        const favoriteCompany = companies.find((c: any) => c.is_favorite)
        if (favoriteCompany) {
          setFavoriteCompanyId(favoriteCompany.id)
        }

        setIsLoading(false)
      } catch (error) {
        console.error('Error loading companies:', error)
        setIsLoading(false)
      }
    }

    loadCompanies()
  }, [user?.id])

  const setActiveCompany = async (company: Company) => {
    setActiveCompanyState(company)
    const token = await getAccessToken()
    if (token && company?.id) {
      saveLastActiveCompanyId(token, company.id)
    }
  }

  const upsertCompany = (company: any) => {
    if (!company?.id) return

    setAvailableCompanies((prev) => {
      const exists = prev.some((existing) => existing.id === company.id)
      if (!exists) return [...prev, company]
      return prev.map((existing) => (existing.id === company.id ? { ...existing, ...company } : existing))
    })

    if (activeCompany?.id === company.id) {
      setActiveCompanyState((prev: any) => ({ ...(prev || {}), ...company }))
    }
  }

  const switchCompany = async (companyId: string) => {
    const company = availableCompanies.find(c => c.id === companyId)
    if (company) {
      setActiveCompany(company)
    }
  }

  const markAsFavorite = (companyId: string) => {
    setAvailableCompanies(prev =>
      prev.map(company => {
        if (company.id === companyId) {
          const newFavoriteStatus = !(company as any).is_favorite
          setFavoriteCompanyId(newFavoriteStatus ? companyId : '')
          return { ...company, is_favorite: newFavoriteStatus }
        }
        if ((company as any).is_favorite && company.id !== companyId) {
          return { ...company, is_favorite: false }
        }
        return company
      })
    )
  }

  const archiveCompany = (companyId: string) => {
    setAvailableCompanies(prev =>
      prev.map(company =>
        company.id === companyId ? { ...company, status: 'archived' as const } : company
      )
    )
  }

  const deleteCompany = (companyId: string) => {
    setAvailableCompanies(prev => prev.filter(company => company.id !== companyId))
    if (activeCompany?.id === companyId) {
      const remaining = availableCompanies.filter(c => c.id !== companyId)
      if (remaining.length > 0) {
        setActiveCompanyState(remaining[0])
      }
    }
  }

  const toggleCompanyStatus = (companyId: string) => {
    setAvailableCompanies(prev =>
      prev.map(company => {
        if (company.id === companyId) {
          const newStatus = (company as any).status === 'active' ? 'inactive' : 'active'
          return { ...company, status: newStatus }
        }
        return company
      })
    )
  }

  const getCompaniesByStatus = (status: 'active' | 'inactive' | 'archived'): Company[] => {
    return availableCompanies.filter(company => (company as any).status === status)
  }

  const getFavoriteCompanies = (): Company[] => {
    return availableCompanies.filter(company => (company as any).is_favorite)
  }

  const value = {
    activeCompany,
    availableCompanies,
    setActiveCompany,
    upsertCompany,
    switchCompany,
    isLoading,
    markAsFavorite,
    archiveCompany,
    deleteCompany,
    toggleCompanyStatus,
    getCompaniesByStatus,
    getFavoriteCompanies,
    favoriteCompanyId,
  }

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
}

export function useCompany() {
  const context = useContext(CompanyContext)
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider')
  }
  return context
}
